import { Gender } from '../../techstyle-shared/react-accounts';
import { getBrandValue, isSavage } from '../utils/brandNameHelper';

/**
 * This is a list of feature flag names that override the default `featureNames` values and
 * is intended to be temporary. See note in `featureNames` about the use of kebab case
 * for the flag name. A flag will temporarily be in both `featureNames` and
 * `savageFeatureNameOverrides` until it is fully migrated to LaunchDarkly, at which point
 *  it can be removed from `savageFeatureNameOverrides`.
 */
const savageFeatureNameOverrides = {
  /**
   * @description Controls whether the Bounceback Endowment feature is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/bounceback-endowment SXF LaunchDarkly configuration }
   */
  BOUNCEBACK_ENDOWMENT: 'bounceback-endowment',
  /**
   * @description Controls whether to show the optional SMS field to Quiz flow.
   *
   * TODO: once the associated experiment is completed this feature flag should be renamed to kebab case in
   * LaunchDarkly and removed from this override object.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/sms_quiz SXF LaunchDarkly configuration }
   */
  SMS_QUIZ: 'sms_quiz',
  /**
   * @description Controls whether user should see the size questions on the PPP, or just skip to the signup form.
   *
   * TODO: once the associated experiment is completed this feature flag should be renamed to kebab case in
   * LaunchDarkly and removed from this override object.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/skip_ppp_sizing SXF LaunchDarkly configuration }
   */
  SKIP_PPP_SIZING: 'skip_ppp_sizing',
} as const;

/**
 * NOTE: When updating a flag to be handled by LaunchDarkly use kebab case for the flag name
 * and use the flag name in `savageFeatureNameOverrides` if applicable. At the same time,
 * that key can be removed from the `savageFeatureNameOverrides` object.
 * The end goal is to have a single `featureNames` object without any overrides so that we have
 * uniform feature flag names across all brands.
 *
 * NOTE: Currently the JSDoc comments are linking to `/projects/default` for the SXF flags, I am
 * unsure how this works once we have access to both environments. If the format changes, it should
 * be a simple find and replace once we have more information.
 */
const featureNames = {
  /**
   * @description Controls whether the accessibility widget is enabled on the site.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/accessibility-ai-widget LaunchDarkly configuration }
   */
  ACCESSIBILITY_AI_WIDGET: 'accessibility-ai-widget',
  /**
   * @description Controls whether Afterpay, Klarna, both or neither payment methods
   * are available to the customer.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/additional-payment-methods SXF LaunchDarkly configuration }
   */
  ADDITIONAL_PAYMENT_METHODS: 'additional-payment-methods',
  /**
   * @description Controls if Afterpay is available as a payment method.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/afterpay LaunchDarkly configuration }
   */
  AFTERPAY: 'afterpay',
  /**
   * @description Controls whether errors will be logged to Graylog using the `/logging` endpoint.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/allow-frontend-logging LaunchDarkly configuration }
   */
  ALLOW_FRONTEND_LOGGING: 'allow-frontend-logging',
  /**
   * @description Controls if customers can upload photos to with their reviews.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/allow-user-uploaded-review-photos LaunchDarkly configuration }
   */
  ALLOW_USER_UPLOADED_REVIEW_PHOTOS: 'allow-user-uploaded-review-photos',
  /**
   * @description Controls whether the Bounceback Endowment feature is enabled. Cannot be migrated to LaunchDarkly
   * for peak 2025 as this is used throughout the backend and LaunchDarkly is not yet supported in backend repos.
   *
   * - {@link https://cms-fl.techstyle.net/?action=features.view&id=7357677519757122758 FL CMS Feature Creature configuration }
   */
  BOUNCEBACK_ENDOWMENT: 'bounceback_endowment',
  /**
   * @description Used to test bot validation work.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/bot-test FL LaunchDarkly configuration }
   */
  BOT_TEST: 'bot-test',
  /**
   * @description Controls whether the Build Your Own Bundle feature is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/build-your-own-bundle LaunchDarkly configuration }
   */
  BUILD_YOUR_OWN_BUNDLE: 'build-your-own-bundle',
  /**
   * @description Controls whether the Cancel Flow Style Test feature is enabled.
   * This is for an A/B test being ran by SXF that can be removed/made permanent after the test is complete.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/cancel-flow-style-test SXF LaunchDarkly configuration }
   */
  CANCEL_FLOW_STYLE_TEST: 'cancel-flow-style-test',
  /**
   * @description Controls whether the Cancel Order Popup is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/cancel-order-popup FL LaunchDarkly configuration }
   */
  CANCEL_ORDER_POPUP: 'cancel-order-popup',
  /**
   * @description This feature flag controls whether Constructor Grid Revamp V1.1 is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/use-constructor-grid-v1-1 LaunchDarkly configuration }
   */
  CONSTRUCTOR_GRID_V1_1: 'use-constructor-grid-v1-1',
  /**
   * @description Controls whether the Constructor Search Grid Revamp V1.1 is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/use-search-grid-v1-1 LaunchDarkly configuration }
   */
  SEARCH_GRID_V1_1: 'use-search-grid-v1-1',
  /**
   * @description Controls whether the Denim Interest modal is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/denim-interest FL LaunchDarkly configuration }
   */
  DENIM_INTEREST: 'denim-interest',
  /**
   * @description Controls whether Disco is enabled on the Order Confirmation page.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/disco-order-confirmation SXF LaunchDarkly configuration }
   */
  DISCO_ORDER_CONFIRMATION: 'disco-order-confirmation',
  /**
   * @description Controls whether the first member billing modal is shown on web.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/fmbe-pop-up-web LaunchDarkly configuration }
   */
  FIRST_MEMBER_BILLING_MODAL: 'fmbe-pop-up-web',
  /**
   * @description Controls whether the Genesys is used as the web chat widget on the site.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/genesys-web-chat LaunchDarkly configuration }
   */
  GENESYS_WEB_CHAT: 'genesys-web-chat',
  /**
   * @description Controls whether the Cognigy widget is used as the web chat widget on the site.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/cognigy-widget LaunchDarkly configuration }
   */
  COGNIGY_WEB_CHAT: 'cognigy-widget',
  /**
   * @description Controls whether the Happy Returns feature is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/happy-returns SXF LaunchDarkly configuration }
   */
  HAPPY_RETURNS: 'happy-returns',
  /**
   * @description Controls whether the Khloe skip the month content is displayed.
   *
   * - {@link none} Statically defined feature for FLW only
   */
  KHLOE_SKIP_THE_MONTH: 'khloe-skip-the-month',
  /**
   * @description Controls if Klarna is available as a payment method.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/klarna LaunchDarkly configuration }
   */
  KLARNA: 'klarna',
  /**
   * @description Controls whether the loyalty program is enabled.
   *
   * - {@link none} Statically defined feature for FL VIPs only
   */
  LOYALTY_PROGRAM_FL: 'loyalty-program-fl',
  /**
   * @description Controls whether the 59.95 membership price increase is enabled.
   *
   * - {@link none} Statically defined feature for FL .com, .ca, and .co.uk only
   */
  MEMBERSHIP_5995_PRICE: 'membership-5995-price',
  /**
   * @description Controls whether the online cancel chat popup is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/online-cancel-chat-popup LaunchDarkly configuration }
   */
  ONLINE_CANCEL_POPUP: 'online-cancel-chat-popup',
  /**
   * @description Controls whether the postreg timer displays for the one hour after a user creates an account.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/countdown-timer LaunchDarkly configuration }
   */
  COUNTDOWN_TIMER: 'countdown-timer',
  /**
   * @description Controls whether the grid promo pricing enhancement is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/prioritized-promos FL LaunchDarkly configuration }
   */
  PRIORITIZED_PROMOS: 'prioritized-promos',
  /**
   * @description Controls whether the retail price test is enabled.
   *
   * - {@link none} Statically defined feature for non-VIPs
   */
  RETAILPRICETEST: 'RETAILPRICETEST',
  /**
   * @description Controls whether users can return in store.
   *
   * - {@link none} Statically defined feature for FL .com only
   */
  RETURN_IN_STORE: 'return-in-store',
  /**
   * @description Controls whether the Scrubs experience is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/scrubs FL LaunchDarkly configuration }
   */
  SCRUBS: 'scrubs',
  /**
   * @description Controls whether the the PDP OOS carousel is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/show-oos FL LaunchDarkly configuration }
   */
  SHOW_OOS: 'show-oos',
  /**
   * @description Single ATB on PDP & VIP opt-in in checkout (w enhanced UI)
   *
   * - {@link none} Statically defined feature as off for all brands
   */
  SINGLE_CTA_VIP_OPT_IN: 'single-cta-vip-opt-in',
  /**
   * @description Controls whether the optional SMS field in Speedy Sign-Up flow is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/sms-speedy LaunchDarkly configuration }
   */
  SMS_SPEEDY: 'sms-speedy',
  /**
   * @description Controls whether the available_size_* should be surfaced as hidden assets.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/surface-hidden-facets SXF LaunchDarkly configuration }
   */
  SURFACE_HIDDEN_FACETS: 'surface-hidden-facets',
  /**
   * @description Controls whether the import tax UI is enabled. Cannot be migrated to LaunchDarkly
   * for peak 2025 as this is used throughout the backend and LaunchDarkly is not yet supported in backend repos.
   *
   * - {@link https://cms-fl.techstyle.net/?action=features.view&id=6369283427031582466 FL CMS Feature Creature configuration }
   */
  TARIFF: 'tariff',
  /**
   * @description Controls whether the import tax for VIPs is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/tariff-vip LaunchDarkly configuration }
   */
  TARIFF_VIP: 'tariff-vip',
  /**
   * @description Controls whether text embroidery is enabled.
   *
   * - {@link none} Statically defined feature for FL .com only
   */
  TEXT_EMBROIDERY: 'text-embroidery',
  /**
   * @description Controls whether USPS RMA is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/usps-rma FL LaunchDarkly configuration }
   */
  USPS_RMA: 'usps-rma',
  /**
   * @description Controls whether the video carousel descriptions are enabled on the PDP.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/web-enable-pdp-video-descriptions FL LaunchDarkly configuration }
   */
  ENABLE_PDP_VIDEO_DESCRIPTIONS: 'web-enable-pdp-video-descriptions',
  /**
   * @description Controls whether users who are created as passwordless may login without password. Cannot be
   * migrated to LaunchDarkly for peak 2025 as this is used throughout the backend and LaunchDarkly is not yet
   * supported in backend repos.
   *
   * - {@link https://cms-fl.techstyle.net/?action=features.view&id=7521607237916213815 FL CMS Feature Creature configuration }
   */
  PASSWORDLESS_LOGIN: 'passwordless_login',
  /**
   * @description Controls whether users are allowed to signup without a password. Cannot be migrated to LaunchDarkly for
   * peak 2025 as this is used throughout the backend and LaunchDarkly is not yet supported in backend repos.
   *
   * - {@link https://cms-fl.techstyle.net/?action=features.view&id=7521608099359148055 FL CMS Feature Creature configuration }
   */
  PASSWORDLESS_SIGNUP: 'passwordless_signup',
  /**
   * @description Controls whether debug tools for the PPP are displayed.
   *
   * - {@link none} Statically defined in code to be enabled only if running the local dev server for all brands
   */
  PROMO_PICKER_DEBUG: 'promo-picker-debug',
  /**
   * @description Controls whether the Promo Picker sign up modal (PPP, aka "Gift Box Sign Up Modal") is enabled
   * and collecting sizes.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/gift-box-sign-up-modal LaunchDarkly configuration }
   */
  PROMO_PICKER_SIGNUP_MODAL: 'gift-box-sign-up-modal',
  /**
   * @description Controls whether the Promo Picker sign up modal (PPP, aka "Gift Box Sign Up Modal") asks for SMS number
   * during sign up.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/gift-box-sign-up-sms LaunchDarkly configuration }
   */
  PROMO_PICKER_SMS: 'gift-box-sign-up-modal-sms',
  /**
   * @description Controls whether the Warehouse Outlet site is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/warehouse-outlet-site FL LaunchDarkly configuration }
   */
  WAREHOUSE_OUTLET_SITE: 'warehouse-outlet-site',
  /**
   * @description Controls whether the cancellation promo offers are enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/save-offer-segmentation FL LaunchDarkly configuration }
   */
  SAVE_OFFER_SEGMENTATION: 'save-offer-segmentation',
  /**
   * @description Controls whether the snooze feature is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/snooze-ab-test FL LaunchDarkly configuration }
   */
  SNOOZE_AB_TEST: 'snooze-ab-test',
  /**
   * @description Controls whether the dropdown is the "tiered" style for "How did you hear?"
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/hdyh-tiered-list LaunchDarkly configuration }
   */
  HDYH_TIERED_LIST: 'hdyh-tiered-list',
  /**
   * @description Controls whether the `/shoppable-video/feed` page is enabled and allowed to be accessed
   * on mobile resolution.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/shoppable-video LaunchDarkly configuration }
   */
  SHOPPABLE_VIDEO: 'shoppable-video',
  /**
   * @description Controls whether the shop by color feature is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/shop-by-color FL LaunchDarkly configuration }
   */
  SHOP_BY_COLOR: 'shop-by-color',
  /**
   * @description Controls whether the image service is used for product images
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/use-image-service-url LaunchDarkly configuration }
   */
  SHOULD_USE_IMAGE_SERVICE_URL: 'use-image-service-url',
  /**
   * @description Controls which click ids are tracked as query params and sent with `identify` calls
   * through Segment when logged in.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/tracked-click-ids SXF LaunchDarkly configuration }
   */
  TRACKED_CLICK_IDS: 'tracked-click-ids',
  /**
   * @description Controls whether the loyalty points for actions feature is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/web-enable-points-for-actions FL LaunchDarkly configuration }
   */
  WEB_ENABLE_POINTS_FOR_ACTIONS: 'web-enable-points-for-actions',
  /**
   * @description Controls whether "incentivized reviews" UI is displayed.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/incentivized-reviews-ui LaunchDarkly configuration }
   */
  INCENTIVIZED_REVIEWS_UI: 'incentivized-reviews-ui',
  /**
   * @description Controls whether to show and process restocking fee for paygo customers.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/paygo-restocking-fee SXF LaunchDarkly configuration }
   */
  PAYGO_RESTOCKING_FEE: 'paygo-restocking-fee',
  /**
   * @description Controls whether the ability to remove a product based on its origin (tag) is enabled. Cannot be
   * migrated to LaunchDarkly for peak 2025 as this is used throughout the backend and LaunchDarkly is not yet
   * supported in backend repos.
   *
   * - {@link https://cms-fl.techstyle.net/?action=features.view&id=7996596515659366971 FL CMS Feature Creature configuration }
   */
  REMOVE_ORIGIN: 'remove_origin',
  /**
   * @description Controls whether to show the optional SMS field to Quiz flow.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/sms-quiz FL LaunchDarkly configuration }
   */
  SMS_QUIZ: 'sms-quiz',
  /**
   * @description Controls whether a quiz promo title is shown in the header.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/sms-header SXF LaunchDarkly configuration }
   */
  QUIZ_PROMO_TITLE: 'sms-header',
  /**
   * @description Controls whether to show the free shipping threshold reference in the mini-cart.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/shipping-threshold-ui-minicart FL LaunchDarkly configuration }
   */
  SHIPPING_THRESHOLD_UI_MINICART: 'shipping-threshold-ui-minicart',
  /**
   * @description Controls whether the video carousel is displayed on the PDP (as seen on you section)
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/pdp-video-carousel LaunchDarkly configuration }
   */
  PDP_VIDEO_CAROUSEL: 'pdp-video-carousel',
  /**
   * @description Controls whether user will see free shipping progress bar in shopping bag page
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/free-shipping-progress LaunchDarkly configuration }
   */
  FREE_SHIPPING_PROGRESS: 'free-shipping-progress',
  /**
   * @description Controls whether order history, react checkout confirmation, etc. uses the image service. Cannot
   * be migrated to LaunchDarkly for peak 2025 as this is used throughout the backend and LaunchDarkly is not yet
   * supported in backend repos.
   *
   * - {@link https://cms-fl.techstyle.net/?action=features.view&id=8225864780448042869 FL CMS Feature Creature configuration }
   * - {@link https://cms-sxf.techstyle.net/?action=features.view&id=8118704353436548889 SXF CMS Feature Creature configuration }
   */
  USE_IMAGE_SERVICE_MISC: 'use_image_service_misc',
  /**
   * @description Controls whether to use Builder as the source for the category side navigation or Navigationizer
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/builder-category-side-nav LaunchDarkly configuration }
   */
  BUILDER_CATEGORY_SIDE_NAV: 'builder-category-side-nav',
  /**
   * @description Controls whether the override on video codec is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/force-hls LaunchDarkly configuration }
   */
  FORCE_HLS: 'force-hls',
  /**
   * @description Controls what the value of the maximum buffer length is.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/hls-max-buffer-length LaunchDarkly configuration }
   */
  HLS_MAX_BUFFER_LENGTH: 'hls-max-buffer-length',
  /**
   * @description Controls whether we serve the account side nav from Builder or Navigationizer
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/use-builder-account-nav LaunchDarkly configuration }
   */
  USE_BUILDER_ACCOUNT_NAV: 'use-builder-account-nav',
  /**
   * @description Controls whether the Member Tariff is enabled. Cannot be migrated to LaunchDarkly for peak 2025
   * as this is used throughout the backend and LaunchDarkly is not yet supported in backend repos.
   *
   * TODO: Sunset feature flag for Member Credit Price Test defined so it can be statically disabled.
   * This should be removed during React checkout migration.
   *
   * - {@link https://cms-sxf.techstyle.net/?action=features.view&id=8004242791930577467 SXF CMS Feature Creature configuration }
   */
  MEMBER_CREDIT_PRICE_TEST: 'SXF-MC-TARIFF-TEST',
  /**
   * @description Controls whether user should see the size questions on the PPP, or just skip to the signup form
   *
   * - {@link none} Statically defined in code to be disabled for Fabletics. Please see `savageFeatureNameOverrides` for
   * Savage override.
   */
  SKIP_PPP_SIZING: 'skip-ppp-sizing',

  ...(isSavage() ? savageFeatureNameOverrides : {}),
} as const;

const savageStaticFeatureValidators = {
  [featureNames.BOT_TEST]: false,
  [featureNames.CANCEL_ORDER_POPUP]: false,
  [featureNames.DENIM_INTEREST]: false,
  [featureNames.KHLOE_SKIP_THE_MONTH]: false,
  [featureNames.LOYALTY_PROGRAM_FL]: false,
  [featureNames.MEMBERSHIP_5995_PRICE]: false,
  [featureNames.PRIORITIZED_PROMOS]: true,
  [featureNames.RETURN_IN_STORE]: false,
  [featureNames.SCRUBS]: false,
  [featureNames.SHOW_OOS]: true,
  [featureNames.TEXT_EMBROIDERY]: false,
  [featureNames.USPS_RMA]: true,
  [featureNames.ENABLE_PDP_VIDEO_DESCRIPTIONS]: false,
  [featureNames.PASSWORDLESS_LOGIN]: false,
  [featureNames.PASSWORDLESS_SIGNUP]: false,
  [featureNames.WAREHOUSE_OUTLET_SITE]: false,
  [featureNames.SAVE_OFFER_SEGMENTATION]: false,
  [featureNames.SNOOZE_AB_TEST]: false,
  [featureNames.SHOP_BY_COLOR]: false,
  [featureNames.WEB_ENABLE_POINTS_FOR_ACTIONS]: false,
  [featureNames.SINGLE_CTA_VIP_OPT_IN]: false,
  [featureNames.REMOVE_ORIGIN]: false,
  [featureNames.SHIPPING_THRESHOLD_UI_MINICART]: false,
};

/**
 * These are features that can be statically disabled by default
 * (i.e. FL) because it is an SXF exclusive feature.
 * These can be found in SXF LaunchDarkly so we only want to statically disable
 * them if we are not in SXF.
 */
const defaultExclusiveFeatureValidators = {
  [featureNames.ADDITIONAL_PAYMENT_METHODS]: false,
  [featureNames.CANCEL_FLOW_STYLE_TEST]: false,
  [featureNames.TRACKED_CLICK_IDS]: false,
  [featureNames.QUIZ_PROMO_TITLE]: false,
  [featureNames.HAPPY_RETURNS]: true,
  [featureNames.SINGLE_CTA_VIP_OPT_IN]: false,
  [featureNames.MEMBER_CREDIT_PRICE_TEST]: false,
  [featureNames.DISCO_ORDER_CONFIRMATION]: false,
  [featureNames.SURFACE_HIDDEN_FACETS]: false,
  [featureNames.PAYGO_RESTOCKING_FEE]: false,
  [featureNames.SKIP_PPP_SIZING]: false,
};

/*
  adding a validator to this object will circumvent feature creature
  and will not make an API call to retrieve these values when using
  local utilities getHasFeature and useHasFeature.

  READ BEFORE ADDING TO THIS LIST:
  When moving features into this list: please validate with a staff
  engineer that this is the appropriate place to put your feature
  and that the feature is now considered a statically defined feature.

  If adding a feature to this list that is permanently enabled or
  disabled: please create a tech debt or squad ticket to clean up
  the path that is no longer used. Please note the deprecation ticket
  in a comment above your feature.
*/
export const staticFeatureValidators = {
  [featureNames.KHLOE_SKIP_THE_MONTH]: ({ gender }) => {
    return gender === Gender.FEMALE;
  },
  [featureNames.LOYALTY_PROGRAM_FL]: ({ membership }) => {
    return membership.isVip;
  },
  [featureNames.MEMBERSHIP_5995_PRICE]: ({ tld }) => {
    return ['.com', '.ca', '.co.uk'].includes(tld);
  },
  [featureNames.RETAILPRICETEST]: ({ membership }) => {
    return !membership.isVip;
  },
  [featureNames.RETURN_IN_STORE]: ({ tld }) => {
    return ['.com'].includes(tld);
  },
  [featureNames.TEXT_EMBROIDERY]: ({ tld }) => {
    return ['.com'].includes(tld);
  },
  [featureNames.PROMO_PICKER_DEBUG]: () =>
    window?.location.hostname === 'localhost',
  ...(isSavage()
    ? savageStaticFeatureValidators
    : defaultExclusiveFeatureValidators),
};

export const globalPrefetchedFeatures = [
  featureNames.BOUNCEBACK_ENDOWMENT,
  featureNames.PASSWORDLESS_LOGIN,
  featureNames.PASSWORDLESS_SIGNUP,
  featureNames.REMOVE_ORIGIN,
  featureNames.TARIFF,
];

export const prefetchedFeaturesInLaunchDarkly = getBrandValue({
  default: [],
  savage: [featureNames.BOUNCEBACK_ENDOWMENT],
});

export default featureNames;
